
import { useState } from 'react';
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";

// Define the form schema
const formSchema = z.object({
  name: z.string().min(2, {
    message: "Name must be at least 2 characters.",
  }),
  email: z.string().email({
    message: "Please enter a valid email address.",
  }),
  company: z.string().optional(),
  phone: z.string().optional(),
  message: z.string().min(5, {
    message: "Message must be at least 5 characters.",
  }),
});

// Define form values type
export type ContactFormValues = z.infer<typeof formSchema> & {
  attachment?: File | null;
};

export const useContactForm = (successTitle: string, successMessage: string) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [attachment, setAttachment] = useState<File | null>(null);

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      company: "",
      phone: "",
      message: "",
    },
  });

  // Simple function to encode form data for email submission
  const encodeFormData = (data: any) => {
    return Object.keys(data)
      .map(key => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
      .join("&");
  }

  const handleSubmit = async (values: ContactFormValues) => {
    setIsSubmitting(true);
    
    try {
      // Prepare form data for submission
      const formData = new FormData();
      
      // Add form fields to formData
      Object.entries(values).forEach(([key, value]) => {
        if (value) formData.append(key, value);
      });
      
      // Add attachment if any
      if (attachment) {
        formData.append('attachment', attachment);
      }

      // Create data object for email submission
      const emailData = {
        name: values.name,
        email: values.email,
        company: values.company || 'Not specified',
        phone: values.phone || 'Not specified',
        message: values.message,
        _subject: `Contact Form Submission from ${values.name}`,
        _replyto: values.email
      };
      
      // Send email to info@future-level.ch using FormSubmit service
      // FormSubmit is a free endpoint that forwards form submissions to an email
      const response = await fetch('https://formsubmit.co/info@future-level.ch', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/json'
        },
        body: encodeFormData(emailData)
      });
      
      if (!response.ok) {
        throw new Error('Server error');
      }
      
      // Show success message
      toast({
        title: successTitle,
        description: successMessage,
        duration: 5000,
      });
      
      setIsSuccess(true);
      
      // Reset form after successful submission
      setTimeout(() => {
        setIsSuccess(false);
        form.reset();
        setAttachment(null);
      }, 3000);
    } catch (error) {
      console.error('Error submitting form:', error);
      
      toast({
        title: "Error",
        description: "There was a problem submitting your form. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    form,
    isSubmitting,
    isSuccess,
    handleSubmit,
    attachment,
    setAttachment,
    handleCaptchaChange: () => {}, // Empty function to maintain API compatibility
    captchaToken: "mock-token",
    captchaError: false
  };
};
